// Déploiement et activité (M24, lot L6-A) : modules activés, utilisateurs actifs, services numérisés,
// interventions, projets, délibérations transmises et anomalies par collectivité. Une valeur indisponible
// est écrite « indisponible » avec son motif (module non activé, non renseigné, source absente).
import { EtatVide, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useActivite, useDeploiement, useRepertoire, type Filtres, type LigneDeploiement } from './api';
import { EtatSources, FiltresTerritoire, NavigationSupervision, provincesDu } from './Communs';
import { dateCalcul, LIBELLES_DEPLOIEMENT, moisCourant, TON_DEPLOIEMENT, valeur } from './logique';
import styles from '../achats/achats.module.css';

type Activite = Record<string, unknown> & { collectivite_id: string; nom: string; fictive: boolean; indisponibles: string | null };

const COLONNES_ACTIVITE: [string, string][] = [
  ['demarches_publiees', 'Démarches publiées (DOS-09)'],
  ['dossiers_en_ligne', 'Dossiers déposés en ligne (DOS-10)'],
  ['interventions_cloturees', 'Interventions clôturées (INT-05)'],
  ['projets_en_cours', 'Projets en cours (TRX-02)'],
  ['deliberations_transmises', 'Délibérations transmises (ASS-04)'],
  ['anomalies_ouvertes', 'Anomalies ouvertes (REC-08)'],
];

export function PageDeploiement() {
  const mois = moisCourant();
  const [filtres, setFiltres] = useState<Filtres>({ mois_debut: mois, mois_fin: mois });
  const tout = useRepertoire({});
  const { provinces, imposee } = provincesDu(tout.data?.perimetre, tout.data?.elements ?? []);
  const deploiement = useDeploiement(filtres);
  const activite = useActivite(filtres);
  const colonnesD: ColonneTableau<LigneDeploiement>[] = [
    { id: 'nom', entete: 'Collectivité', enTeteLigne: true, masquable: false, cellule: (l) => `${l.nom}${l.fictive ? ' (fictive)' : ''}`, valeurTri: (l) => l.nom },
    { id: 'province', entete: 'Province', cellule: (l) => l.province_code },
    { id: 'statut', entete: 'Déploiement', cellule: (l) => <PuceEtat ton={TON_DEPLOIEMENT[l.statut_deploiement] ?? 'neutre'} libelle={LIBELLES_DEPLOIEMENT[l.statut_deploiement] ?? l.statut_deploiement} /> },
    { id: 'modules', entete: 'Modules activés', cellule: (l) => (l.modules_actifs.length ? `${l.nombre_modules_actifs} (${l.modules_actifs.join(', ')})` : 'aucun') },
    { id: 'utilisateurs', entete: 'Utilisateurs actifs sur 30 jours', numerique: true, cellule: (l) => valeur(l.utilisateurs_actifs_30j, 'nombre') },
    { id: 'derniere', entete: 'Dernière activité', cellule: (l) => dateCalcul(l.derniere_activite) },
  ];
  const colonnesA: ColonneTableau<Activite>[] = [
    { id: 'nom', entete: 'Collectivité', enTeteLigne: true, masquable: false, cellule: (l) => `${l.nom}${l.fictive ? ' (fictive)' : ''}`, valeurTri: (l) => l.nom },
    ...COLONNES_ACTIVITE.map(([cle, entete]) => ({ id: cle, entete, numerique: true, cellule: (l: Activite) => valeur(l[cle], 'nombre') })),
    { id: 'indisponibles', entete: 'Valeurs indisponibles', cellule: (l) => l.indisponibles ?? 'aucune' },
  ];
  const lignesA = (activite.data?.elements ?? []) as Activite[];
  return (
    <>
      <EnTetePage etiquette="Supervision" titre="Déploiement et activité" description="Modules activés, utilisateurs actifs et volumes agrégés par collectivité, sans aucune donnée nominative. Les volumes de période portent sur les mois choisis." />
      <NavigationSupervision actif="deploiement" />
      <FiltresTerritoire filtres={filtres} onChange={setFiltres} avecPeriode provinces={provinces} imposee={imposee} />
      <EtatRequete chargement={deploiement.isPending} erreur={deploiement.error} onReessayer={() => void deploiement.refetch()} lot="L6-A" fonction="Le déploiement">
        <section className={styles.bloc} aria-labelledby="titre-deploiement">
          <h2 id="titre-deploiement">Déploiement</h2>
          <EtatSources couverture={deploiement.data?.couverture} />
          {(deploiement.data?.elements.length ?? 0) === 0 ? (
            <EtatVide titre="Aucune collectivité">Aucune collectivité dans ce périmètre.</EtatVide>
          ) : (
            <Tableau legende="Déploiement des collectivités" colonnes={colonnesD} lignes={deploiement.data?.elements ?? []} cleLigne={(l) => l.collectivite_id} cleColonnes="supervision-deploiement" />
          )}
        </section>
      </EtatRequete>
      <EtatRequete chargement={activite.isPending} erreur={activite.error} onReessayer={() => void activite.refetch()} lot="L6-A" fonction="L'activité agrégée">
        <section className={styles.bloc} aria-labelledby="titre-activite">
          <h2 id="titre-activite">Activité {activite.data?.periode ? `de ${activite.data.periode.mois_debut} à ${activite.data.periode.mois_fin}` : ''}</h2>
          <EtatSources couverture={activite.data?.couverture} />
          {lignesA.length === 0 ? (
            <EtatVide titre="Aucune collectivité">Aucune collectivité dans ce périmètre.</EtatVide>
          ) : (
            <Tableau legende="Activité agrégée par collectivité" colonnes={colonnesA} lignes={lignesA} cleLigne={(l) => l.collectivite_id} cleColonnes="supervision-activite" />
          )}
        </section>
      </EtatRequete>
    </>
  );
}
